<script>
  import { tick } from 'svelte';
  import BaseButton from '../BaseButton/BaseButton.svelte';
  import BaseLink from '../BaseLink/BaseLink.svelte';
  import { formatPhone } from '../../utils/phoneFormatter.ts';
  import { getStaticMapImageSrc } from '../../utils/static-map.ts';
  import { SPRITESHEET_URL } from '../../utils/constants.ts';
  import { page } from '$app/stores';

  let {
    lat,
    lng,
    name,
    addressLine1,
    addressLine2 = undefined,
    city,
    stateAbbrev,
    phone = undefined,
    distance = undefined,
    zip,
    isGeneric = false,
    alt,
    url,
    open = undefined,
    onClick,
    deferMap = undefined,
    lazyloadImage = false,
    dataTestId = 'pharmacy-location-action'
  } = $props();

  let defaultSrc = $state();
  let highResSrc = $state();
  let largeDefaultSrc = $state();
  let largeHighResSrc = $state();

  const getMapImage = () => {
    if (lat && lng && !deferMap) {
      const userAgent = $page.data && $page.data.userAgent;
      const mapboxDetails = {
        lat,
        lng,
        userAgent
      }
      const smallOptions = { width: 358, height: 96, ...mapboxDetails };
      const largeOptions = { width: 480, height: 96, ...mapboxDetails };

      defaultSrc = getStaticMapImageSrc(smallOptions);
      highResSrc = getStaticMapImageSrc({ ...smallOptions, highRes: true });
      largeDefaultSrc = getStaticMapImageSrc(largeOptions);
      largeHighResSrc = getStaticMapImageSrc({ ...largeOptions, highRes: true });
    }
  };

  getMapImage();

  let component = $derived(onClick ? BaseButton : BaseLink);
  let componentProps = $derived(onClick ? { onClick } : { url });

  $effect(() => {
    tick().then(() => {      
      getMapImage()
    });
  });

  const SvelteComponent = $derived(component);
</script>

<SvelteComponent
  className="w-full h-full flex flex-col rounded-lg relative active:shadow-active shadow-y-sm pseudo-shadow-tight-hover bg-white mx-auto max-w-120 group"
  {dataTestId}
  itemscope={true}
  itemtype="http://schema.org/Pharmacy"
  {...componentProps}
>
  {#if lat && lng && !deferMap}
    <div class="rounded-t-lg overflow-hidden w-full skeleton-gradient h-24">
      <picture>
        <source media="(min-width: 1024px)" srcset={`${defaultSrc}, ${highResSrc} 2x`} />
        <source media="(min-width: 768px)" srcset={`${largeDefaultSrc}, ${largeHighResSrc} 2x`} />
        <source media="(min-width: 640px)" srcset={`${defaultSrc}, ${highResSrc} 2x`} />
        <source media="(min-width: 422px)" srcset={`${largeDefaultSrc}, ${largeHighResSrc} 2x`} />
        <source media="(max-width: 421px)" srcset={`${defaultSrc}, ${highResSrc} 2x`} />
        <img
          class="object-cover w-full h-full a11y-no-transform block duration-75 transition-transform ease-in hover-hover:group-hover:scale-105 group-active:scale-105"
          loading={lazyloadImage === true ? 'lazy' : 'eager'}
          src={defaultSrc}
          {alt}
          itemprop="hasMap"
        />
      </picture>
    </div>
  {/if}
  <div class="flex justify-between grow w-full">
    <div class="w-full flex flex-col">
      <div class="flex justify-between grow">
        <div class="text-left p-4 flex-auto break-words" class:pb-6={!phone && !open}>
          <h3 class="text-lg leading-tight text-blue-navy mb-2" itemprop="name">{name}</h3>
          <div
            class="text-sm text-gray-brownish leading-tight uppercase"
            itemprop="address"
            itemscope={true}
            itemtype="http://schema.org/PostalAddress"
          >
            <div itemprop="streetAddress">{addressLine1}</div>
            {#if addressLine2}
              <div itemprop="streetAddress">{addressLine2}</div>
            {/if}
            <div>
              <span itemprop="addressLocality">{city} ,</span>
              <span itemprop="addressRegion">{stateAbbrev}</span>
              <span itemprop="postalCode">{zip}</span>
            </div>
          </div>
        </div>
        {#if distance}
          <div class="flex grow-0 shrink-0 justify-center p-4" class:items-center={!phone}>
            <p class="text-sm">{distance}mi</p>
          </div>
        {/if}
      </div>
      {#if phone || open}
        <div class="border-t border-gray-pale mx-4 flex justify-between">
          {#if phone}
            <p
              class="py-2 text-sm font-semibold text-blue-cerulean hover-hover:group-hover:text-blue-darkish hover-hover:hover:underline group-active:text-blue-darkish active:underline"
              itemprop="telephone"
              dataTestId="pharmacy-location-information-phone-link"
            >
              {formatPhone(phone)}
            </p>
          {/if}
          {#if open}
            <span class="py-2 text-sm font-semibold ml-auto">{open}</span>
          {/if}
        </div>
      {/if}
    </div>
    <div
      class="flex flex-none items-center justify-center w-6 {lat && lng
        ? 'rounded-br-lg'
        : 'rounded-r-lg'} {!isGeneric ? 'bg-gray-pale' : 'bg-blue-very-light'}"
    >
      <svg
        class="h-4 w-4 fill-current text-blue-cerulean a11y-no-transform block duration-75 transition-transform ease-in
        hover-hover:group-hover:scale-125 group-active:scale-125"
        role="presentation"
      >
        <use xlink:href="{SPRITESHEET_URL}chevron-right" />
      </svg>
    </div>
  </div>
</SvelteComponent>
